import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Instagram, MapPin } from "lucide-react";
import { api, errMsg, fmtCount, fmtLocation } from "@/lib/api";
import { LocationFields } from "@/pages/Join";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { IgEmbed } from "@/components/IgEmbed";
import { Avatar } from "@/components/Avatar";

const label = "font-mono text-[10px] uppercase tracking-widest text-stone-500";

const ProfileCard = ({ user, setUser }) => {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/me/refresh"); setUser(data); toast.success("Updated from Instagram"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="dashboard-profile-card" className="rounded-[2rem] bg-[#181615] p-8 text-white">
      <div className="flex items-center gap-4">
        <div className="ig-ring"><div className="rounded-full border-2 border-[#181615]"><Avatar user={user} className="h-20 w-20" textClass="text-2xl" /></div></div>
        <div className="min-w-0">
          <p data-testid="dashboard-name" className="truncate font-serif text-2xl">{user.name || user.username}</p>
          <p className="font-mono text-xs text-stone-400">@{user.username}{user.followers_count ? ` · ${fmtCount(user.followers_count)} followers` : ""}</p>
        </div>
      </div>
      {user.biography && <p data-testid="dashboard-bio" className="mt-6 whitespace-pre-line text-sm leading-relaxed text-stone-300">{user.biography}</p>}
      {fmtLocation(user) && <p data-testid="dashboard-location" className="mt-4 flex items-center gap-2 text-sm text-stone-300"><MapPin className="h-4 w-4" />{fmtLocation(user)}</p>}
      <p className="mt-6 flex items-center gap-2 text-xs text-stone-400"><Instagram className="h-3.5 w-3.5" /> Your picture, name and bio come straight from Instagram. Change them there.</p>
      <button data-testid="refresh-instagram-button" onClick={refresh} disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm text-stone-900 hover:bg-pink-200 disabled:opacity-60">
        <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Updating…" : "Update from Instagram"}
      </button>
    </div>
  );
};

const LocationSetup = ({ setUser }) => {
  const [loc, setLoc] = useState({ city: "", state: "", zip: "" });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/me/location", loc); setUser(data); toast.success("Location saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} data-testid="location-setup" className="mb-8 space-y-4 rounded-[2rem] border border-pink-200 bg-pink-50 p-6">
      <p className="text-sm">Add where you work so clients near you can find you. You can only set this once.</p>
      <LocationFields loc={loc} setLoc={setLoc} />
      <button data-testid="location-save-button" className="ig-gradient rounded-full px-6 py-2.5 text-sm text-white">Save location</button>
    </form>
  );
};

export default function Dashboard() {
  const { user, setUser, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh]" />;
  if (!user) return <Navigate to="/join" replace />;
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p data-testid="dashboard-username" className="flex items-center gap-2 font-mono text-sm text-stone-500">@{user.username} <RoleBadge role={user.role} /></p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl">Hey, {user.name || user.username}</h1>
        </div>
        {user.role === "tech"
          ? <Link to={`/tech/${user.username}`} data-testid="view-public-profile" className="inline-flex items-center gap-1 text-sm text-pink-600 hover:underline">View my public profile <ExternalLink className="h-3.5 w-3.5" /></Link>
          : <Link to="/explore" data-testid="dashboard-find-techs" className="text-sm text-pink-600 hover:underline">Find nail techs</Link>}
      </div>
      <div className="mt-10">{user.role === "tech" && !user.zip && <LocationSetup setUser={setUser} />}</div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5"><ProfileCard user={user} setUser={setUser} /></div>
        <div className="lg:col-span-7">
          <p className={`${label} mb-3`}>Your posts, live from Instagram</p>
          <div className="overflow-hidden rounded-[2rem] border border-[#EAE4DC]"><IgEmbed username={user.username} rows={2} /></div>
        </div>
      </div>
    </div>
  );
}
